import React from 'react';
import { reportFrontendError, AUTO_ERROR_PATH } from '../lib/errorReporter.js';

// Phase 2 — top-level React Error Boundary. Catches render errors,
// reports them to /api/feedback/auto-error, and shows a CIVIX-themed
// fallback so users aren't staring at a blank page.

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[CIVIX ErrorBoundary caught render error]:', error, info);
    try {
      reportFrontendError({
        errorMessage: error?.message || String(error),
        stack: (error?.stack || '') + (info?.componentStack ? `\n--- React stack ---\n${info.componentStack}` : ''),
        source: 'react_error_boundary',
      });
    } catch (_) { /* never throw from a boundary */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch (_) {}
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0A0A0F',
          color: '#fff',
          fontFamily: 'system-ui, Segoe UI, Roboto, sans-serif',
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 480,
            padding: 32,
            borderRadius: 12,
            background: '#13132a',
            border: '1px solid #252550',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              margin: '0 auto 16px',
              background: 'linear-gradient(135deg, #7C3AED, #E040FB)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28,
              fontWeight: 700,
            }}
          >
            !
          </div>
          <h1 style={{ margin: '0 0 8px', color: '#E040FB', fontSize: 22 }}>
            Something went wrong
          </h1>
          <p style={{ color: '#aaa', fontSize: 14, lineHeight: 1.5, margin: '0 0 20px' }}>
            CIVIX hit an unexpected error. The team has been notified automatically.
            Try reloading — if it keeps happening, we&rsquo;ll look into it.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={this.handleReload}
              style={{
                padding: '10px 18px',
                borderRadius: 8,
                border: 'none',
                background: '#3B82F6',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reload page
            </button>
            <button
              onClick={() => { window.location.href = '/dashboard'; }}
              style={{
                padding: '10px 18px',
                borderRadius: 8,
                border: '1px solid #333366',
                background: 'transparent',
                color: '#aaa',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Go to Dashboard
            </button>
          </div>
          {this.state.error && (
            <details style={{ marginTop: 24, textAlign: 'left', background: '#0a0a14', padding: 12, borderRadius: 8, border: '1px solid #1f1f3a' }}>
              <summary style={{ cursor: 'pointer', color: '#ff6b6b', fontSize: 12, fontWeight: 600 }}>
                {this.state.error.message || 'Error Details'}
              </summary>
              <pre style={{ marginTop: 8, fontSize: 11, color: '#888', whiteSpace: 'pre-wrap', maxHeight: 150, overflowY: 'auto' }}>
                {this.state.error.stack || String(this.state.error)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export { AUTO_ERROR_PATH };
